




































import Cookies from 'js-cookie';
import { defineComponent, ref, PropType } from '@vue/composition-api';
import { queryToJson, getImgSrcByEnv, imgSrcPrefix, fileNameFormatter, camelCaseFormDataDeep } from '@tt4b/ttam-utils';
import { ImageToUpload, ImageMaterial, FileUploadStatus, AdgroupFormData, FullNormalFormData } from '@tt4b/ttam-enums';
import { apis, useUploadImageApi, useUploadImageToLibraryServiceApi } from '@tt4b/ttam-hooks';
import ImageDialog from './dialog/index.vue';
import { isFunction } from 'lodash-es';

export default defineComponent({
  name: 'UploadImage',
  components: { ImageDialog },
  props: {
    limit: { type: Number, default: 20 },
    /** 是否需要多选 */
    multiple: { type: Boolean, default: true },
    /** 支持的图片类型，为空则为不限制 */
    imageModeSupport: { type: Array, default: () => [] },
    /** 是否需要版位校验 */
    inventoryCheck: { type: Boolean, default: true },
    /** 是否是上传封面模式 */
    isUploadCover: { type: Boolean, default: false },
    /** 确认事件，如果传入确认事件，则不会调用默认 */
    confirmCallback: {
      type: Function as PropType<(param: any) => void>,
      default: undefined,
    },
    adgroupData: {
      required: true,
      type: Object as PropType<AdgroupFormData>,
      default: () => ({}),
    },
    creativeData: {
      required: true,
      type: Object as PropType<FullNormalFormData>,
      default: () => ({}),
    },
  },

  setup(props, { emit }) {
    const { aadvid } = queryToJson();

    const imageFail = new Set();
    const imageSuccess = new Set();
    let imageSuccessFiles: ImageMaterial[] = [];
    const files = ref<ImageToUpload[]>();
    const acceptFileType = ref('image/png,image/jpeg,image/jpg');

    /** 上传dialog组件节点 */
    const uploadDialogNode = ref();
    /** 上传按钮节点 */
    const uploadButtonNode = ref();
    /** 上传时需要添加的 Headers */
    const uploadHeaders = ref({ 'X-CSRFToken': Cookies.get('csrftoken') });
    /** 上传时的地址 */
    const uploadImageUrl = ref(`${apis.material.uploadImage}?aadvid=${aadvid}`);

    /** vi-upload 节点 */
    const uploadNode = ref();
    const submit = () => {
      uploadNode.value?.submit();
    };
    const clearFiles = () => {
      files.value = [];
      uploadNode.value?.clearFiles();
    };

    function onFilesChange(changedFiles) {
      files.value = changedFiles;
    }

    function onUploadChange(_file: ImageToUpload, curFiles: ImageToUpload[]) {
      files.value = curFiles.map(item => ({
        ...item,
        croppedCanvas: undefined,
        croppedUrl: undefined,
        uploaderStatus: '',
      }));
    }

    /** 开始选择文件，弹出系统弹窗 */
    function startUpload() {
      uploadButtonNode.value.$el.click();
    }

    /** 从本地选择文件完成事件 */
    function onGetRawFiles(fileRaws) {
      emit('select-files', fileRaws);
    }

    /** 上传成功事件 */
    async function onUploadSuccess(response, file) {
      const res = camelCaseFormDataDeep(response);
      if (res.code !== 0) {
        file.status = FileUploadStatus.Fail;
        uploadDialogNode.value?.changeFileUploaderStatus(file.name, FileUploadStatus.Fail);
        return;
      }
      if (props.isUploadCover) {
        const { name, width, height, size, webUri } = res?.data?.imageInfo ?? {};
        imageSuccessFiles.push({
          name,
          width,
          height,
          size,
          webUri,
          id: '',
          imageUrl: res?.data?.url,
        });
        uploadDialogNode.value?.changeFileUploaderStatus(file.name, FileUploadStatus.Success);
        return;
      }
      const { uid } = file;
      const { imageInfo } = res.data;
      const originImage = files.value?.find(f => f.uid === uid);
      let originImageUri = '';
      if (originImage) {
        uploadDialogNode.value?.changeFileUploaderStatus(file.name, FileUploadStatus.Uploading);
        const uploadResponse = await useUploadImageApi(originImage.raw!);
        originImageUri = uploadResponse?.imageInfo?.webUri ?? '';
      }
      imageInfo.fileNme = fileNameFormatter({
        name: file.name,
        width: imageInfo.width,
        height: imageInfo.height,
      });
      const libraryRes = await useUploadImageToLibraryServiceApi({
        name: file.name,
        webUri: imageInfo.webUri,
        originalWebUri: originImageUri,
        showError: false,
      });
      if (!libraryRes) {
        imageFail.add(uid);
        uploadDialogNode.value?.changeFileUploaderStatus(file.name, FileUploadStatus.Fail);
        return;
      }
      imageSuccess.add(uid);
      const info = {
        id: uid,
        name: file.name,
        width: imageInfo.width,
        height: imageInfo.height,
        size: imageInfo.size,
        webUri: imageInfo.webUri,
        imageUrl: getImgSrcByEnv({ URL: imgSrcPrefix.URL }, imageInfo.webUri),
      };
      imageSuccessFiles.push(info);
      uploadDialogNode.value?.changeFileUploaderStatus(file.name, FileUploadStatus.Success);
    }

    /** 上传失败事件 */
    function onUploadError(res, file) {
      const { uid } = file;
      imageFail.add(uid);
    }

    /** 用于初始化一些变量 */
    function reset() {
      clearFiles();
      imageSuccessFiles = [];
    }

    /** 确认 */
    function onConfirm() {
      if (!imageSuccessFiles.length) {
        reset();
        return;
      }

      if (isFunction(props.confirmCallback)) {
        props.confirmCallback(imageSuccessFiles);
        reset();
        return;
      }

      emit('success', imageSuccessFiles);
      reset();
    }

    return {
      uploadNode,
      uploadDialogNode,
      uploadButtonNode,

      files,
      acceptFileType,
      uploadHeaders,
      uploadImageUrl,

      submit,
      clearFiles,
      startUpload,
      onGetRawFiles,
      onFilesChange,
      onUploadChange,
      onConfirm,
      onUploadSuccess,
      onUploadError,
    };
  },
});
